/**
 * Users service — friends of a user (list/search/add/remove).
 * Ported 1:1 from the legacy flat services/users.js (URL quirks preserved,
 * e.g. the `friends?&per_page` double separator). Shapes verified against
 * docs/users-migration/Api-response/ (user-friends-*.json).
 */
import constants from '../../constants'
import { getChatApiBase, getChatApiHeaders } from '../chatApiClient'
import axios from '../axiosInstance'
import { ChatApp, UpdateFriendsResponse, UsersListResponse } from './types'

export async function friendList(
  id: string,
  app: ChatApp,
  apiHost: string,
  pageNo = 1,
  searchText = '',
): Promise<UsersListResponse> {
  const URL = `${getChatApiBase(app, apiHost)}/users/${id}/friends?&per_page=${
    constants.perPageInListings
  }&page=${pageNo}&searchKey=${searchText}&useCursoredIndex=1`
  return axios
    .get(`${URL}`, {
      headers: getChatApiHeaders(app),
    })
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}

export async function searchFriendList(
  id: string,
  app: ChatApp,
  apiHost: string,
  pageNo = 1,
  searchKey = '',
): Promise<UsersListResponse> {
  const URL = `${getChatApiBase(app, apiHost)}/users/${id}/friends?&per_page=${
    constants.perPageInListings
  }&page=${pageNo}&searchKey=${searchKey}`
  return axios
    .get(`${URL}`, {
      headers: getChatApiHeaders(app),
    })
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}

/** Friend search used by the add-friend modal (legacy name kept — it queries
 *  the friends endpoint despite the `userSearch` name). */
export async function userSearch(
  id: string,
  app: ChatApp,
  apiHost: string,
  searchText: string,
  pageNo = 1,
): Promise<UsersListResponse> {
  const URL = `${getChatApiBase(
    app,
    apiHost,
  )}/users/${id}/friends?searchKey=${searchText}&per_page=${
    constants.perPageInListings
  }&page=${pageNo}&useCursoredIndex=1`
  return axios
    .get(`${URL}`, {
      headers: getChatApiHeaders(app),
    })
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}

/** Add/update friendships. Result is a per-uid map — a failed uid is still an
 *  HTTP 200; callers branch on `data.accepted[uid].success`. */
export async function updateFriends(
  uid: string,
  post: { accepted?: string[] | string; rejected?: string[] | string },
  app: ChatApp,
  apiHost: string,
): Promise<UpdateFriendsResponse> {
  const URL = `${getChatApiBase(app, apiHost)}/users/${uid}/friends`
  return axios
    .post(`${URL}`, post, {
      headers: getChatApiHeaders(app),
    })
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}

export async function removeFriends(
  uid: string,
  post: string[] | string,
  app: ChatApp,
  apiHost: string,
): Promise<UpdateFriendsResponse> {
  const URL = `${getChatApiBase(app, apiHost)}/users/${uid}/friends`
  return axios
    .delete(`${URL}`, {
      headers: getChatApiHeaders(app),
      data: {
        friends: post,
      },
    })
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}
